import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import LoadingFallback from '@components/ui/loading-fallback'

// Layout (caricati subito)
import MainLayout from '@components/layouts/MainLayout'
import ProtectedRoute from '@components/auth/ProtectedRoute'

// Pagine Auth (caricate subito)
import Login from '@pages/Auth/Login'

// Pagine App (caricate subito)
import Dashboard from '@pages/Dashboard'

// Pagine Catalogo
import ProdottiPage from '@pages/Prodotti/ProdottiPage'
const ProdottoForm = lazy(() => import('@pages/Prodotti/ProdottoForm'))
const CategorieList = lazy(() => import('@pages/Categorie/CategorieList'))
const CategoriaForm = lazy(() => import('@pages/Categorie/CategoriaForm'))
const FornitoriList = lazy(() => import('@pages/Fornitori/FornitoriList'))
const FornitoreForm = lazy(() => import('@pages/Fornitori/FornitoreForm'))

// Pagine Box (lazy loaded)
import { 
  BoxList, BoxForm,
  ComponentiList, ComponenteForm,
  ConfigurazioniList, ConfigurazioneForm
} from '@pages/Box'

// Pagine Magazzino (lazy loaded)
import {
  DashboardMagazzino,
  MovimentiList,
  CaricoForm,
  ScaricoForm,
  LottoProduzioneForm,
} from '@pages/Magazzino'

// Pagine Clienti (lazy loaded)
const ClientiList = lazy(() => import('@pages/Clienti/ClientiList'))
const ClienteDetail = lazy(() => import('@pages/Clienti/ClienteDetail'))
const ClienteForm = lazy(() => import('@pages/Clienti/ClienteForm'))
const IndirizzoForm = lazy(() => import('@pages/Clienti/IndirizzoForm'))

// Pagine Ordini (lazy loaded)
import {
  OrdiniList, OrdiniArchivio, OrdineDetail, OrdineWizard,
  // FASE 7.4: Spedizioni Avanzate
  SpedizioniList, SpedizioneDetail,
  DestinatariList, DestinatarioForm,
  ColliList, ColloForm,
  SpedizioneWizard,
} from '@pages/Ordini'

// Pagine Integrazioni (lazy loaded)
const ShopifySyncStatus = lazy(() => import('@pages/Integrazioni/ShopifySyncStatus'))
const ShopifyOAuthConnect = lazy(() => import('@pages/Integrazioni/ShopifyOAuthConnect'))

// Pagine Impostazioni (lazy loaded)
const VenditoriList = lazy(() => import('@pages/Impostazioni/VenditoriList'))

/**
 * Componente principale dell'applicazione.
 * 
 * Gestisce:
 * - Routing dell'applicazione
 * - Protezione route autenticate
 * - Toast notifications (Sonner)
 */
function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Route pubbliche */}
            <Route path="/login" element={<Login />} />

            {/* Route protette */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<Dashboard />} />
              
              {/* Catalogo - Prodotti */}
              <Route path="/prodotti" element={<ProdottiPage />} />
              <Route path="/prodotti/nuovo" element={<ProdottoForm />} />
              <Route path="/prodotti/:id" element={<LegacyDetailRedirect basePath="/prodotti" />} />
              <Route path="/prodotti/:id/modifica" element={<ProdottoForm />} />
              
              {/* Catalogo - Categorie */}
              <Route path="/categorie" element={<CategorieList />} />
              <Route path="/categorie/nuovo" element={<CategoriaForm />} />
              <Route path="/categorie/:id" element={<LegacyDetailRedirect basePath="/categorie" />} />
              <Route path="/categorie/:id/modifica" element={<CategoriaForm />} />
              
              {/* Catalogo - Fornitori */}
              <Route path="/fornitori" element={<FornitoriList />} />
              <Route path="/fornitori/nuovo" element={<FornitoreForm />} />
              <Route path="/fornitori/:id" element={<LegacyDetailRedirect basePath="/fornitori" />} />
              <Route path="/fornitori/:id/modifica" element={<FornitoreForm />} />
              
              {/* Box - Tipologie */}
              <Route path="/box/tipologie" element={<BoxList />} />
              <Route path="/box/tipologie/nuovo" element={<BoxForm />} />
              <Route path="/box/tipologie/assembla" element={<LottoProduzioneForm />} />
              <Route path="/box/tipologie/:id" element={<LegacyDetailRedirect basePath="/box/tipologie" />} />
              <Route path="/box/tipologie/:id/modifica" element={<BoxForm />} />
              
              {/* Box - Configurazioni */}
              <Route path="/box/configurazioni" element={<ConfigurazioniList />} />
              <Route path="/box/configurazioni/nuovo" element={<ConfigurazioneForm />} />
              <Route path="/box/configurazioni/:id" element={<LegacyDetailRedirect basePath="/box/configurazioni" />} />
              <Route path="/box/configurazioni/:id/modifica" element={<ConfigurazioneForm />} />
              
              {/* Box - Componenti */}
              <Route path="/box/componenti" element={<ComponentiList />} />
              <Route path="/box/componenti/nuovo" element={<ComponenteForm />} />
              <Route path="/box/componenti/:id" element={<LegacyDetailRedirect basePath="/box/componenti" />} />
              <Route path="/box/componenti/:id/modifica" element={<ComponenteForm />} />
              
              {/* Magazzino - Dashboard */}
              <Route path="/magazzino" element={<DashboardMagazzino />} />
              
              {/* Magazzino - Movimenti */}
              <Route path="/magazzino/movimenti" element={<MovimentiList />} />
              <Route path="/magazzino/carico" element={<CaricoForm />} />
              <Route path="/magazzino/scarico" element={<ScaricoForm />} />
              
              {/* Magazzino - Lotti Produzione (legacy redirects) */}
              <Route path="/magazzino/lotti-produzione" element={<Navigate to="/box/tipologie" replace />} />
              <Route path="/magazzino/lotti-produzione/nuovo" element={<LegacyPathRedirect to="/box/tipologie/assembla" />} />
              
              {/* Clienti */}
              <Route path="/clienti" element={<ClientiList />} />
              <Route path="/clienti/nuovo" element={<ClienteForm />} />
              <Route path="/clienti/:id" element={<ClienteDetail />} />
              <Route path="/clienti/:id/modifica" element={<ClienteForm />} />
              
              {/* Indirizzi Clienti */}
              <Route path="/clienti/:id/indirizzi/nuovo" element={<IndirizzoForm />} />
              <Route path="/clienti/:id/indirizzi/:indirizzoId/modifica" element={<IndirizzoForm />} />
              
              {/* Ordini */}
              <Route path="/ordini" element={<OrdiniList />} />
              <Route path="/ordini/archivio" element={<OrdiniArchivio />} />
              <Route path="/ordini/nuovo" element={<OrdineWizard />} />
              <Route path="/ordini/:id" element={<OrdineDetail />} />
              <Route path="/ordini/:id/modifica" element={<OrdineWizard />} />
              
              {/* FASE 7.4: Spedizioni Avanzate - Spedizioni */}
              <Route path="/ordini/spedizioni" element={<SpedizioniList />} />
              <Route path="/ordini/spedizioni/:id" element={<SpedizioneDetail />} />
              
              {/* FASE 7.4: Spedizioni Avanzate - Destinatari */}
              <Route path="/ordini/destinatari" element={<DestinatariList />} />
              <Route path="/ordini/destinatari/nuovo" element={<DestinatarioForm />} />
              <Route path="/ordini/destinatari/:id/modifica" element={<DestinatarioForm />} />
              
              {/* FASE 7.4: Spedizioni Avanzate - Colli */}
              <Route path="/ordini/colli" element={<ColliList />} />
              <Route path="/ordini/colli/nuovo" element={<ColloForm />} />
              <Route path="/ordini/colli/:id/modifica" element={<ColloForm />} />
              
              {/* FASE 7.4: Wizard Spedizioni */}
              <Route path="/ordini/:ordineId/spedizione/wizard" element={<SpedizioneWizard />} />
              
              {/* FASE 8.2: Integrazioni Shopify */}
              <Route path="/integrazioni/shopify" element={<ShopifySyncStatus />} />
              <Route path="/integrazioni/shopify/connetti" element={<ShopifyOAuthConnect />} />
              <Route path="/integrazioni/shopify/callback" element={<ShopifyOAuthConnect />} />
              
              {/* Altre pagine */}
              <Route path="/report" element={<PlaceholderPage title="Report" />} />
              <Route path="/impostazioni" element={<PlaceholderPage title="Impostazioni" />} />
              <Route path="/impostazioni/venditori" element={<VenditoriList />} />
              <Route path="/profilo" element={<PlaceholderPage title="Profilo" />} />
            </Route>
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  )
}

/**
 * Componente placeholder per pagine non ancora implementate.
 */
function PlaceholderPage({ title }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="text-muted-foreground mt-2">Pagina in costruzione</p>
    </div>
  )
}

function LegacyDetailRedirect({ basePath }) {
  const { id } = useParams()

  if (!id) {
    return <Navigate to={basePath} replace />
  }

  return <Navigate to={`${basePath}/${id}/modifica`} replace />
}

function LegacyPathRedirect({ to }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search || ''}`} replace />
}

export default App
